import { MonoTypeOperatorFunction, Observable, Subject, defer, retry, throwError, timer } from 'rxjs';
import { finalize } from 'rxjs/operators';

/** HTTP statuses worth retrying: request timeout, throttling and gateway/availability errors. Status 0 is a network failure. */
const TRANSIENT_HTTP_STATUSES: ReadonlySet<number> = new Set<number>([0, 408, 429, 502, 503, 504]);

/**
 * Retries transient HTTP failures with short, jittered exponential backoff (about 300 ms then 600 ms by default).
 * Any other error, such as a 4xx or a server bug, is rethrown immediately because retrying cannot fix it.
 * @param retryCount Maximum number of retries after the first attempt.
 * @param baseDelayMs Delay before the first retry; doubles on each following retry.
 */
export const retryTransient = <T>(retryCount: number = 2, baseDelayMs: number = 300): MonoTypeOperatorFunction<T> =>
  retry<T>({
    count: retryCount,
    delay: (error: unknown, attempt: number) => {
      const status: number | undefined = (error as { status?: number } | null)?.status;
      if (status === undefined || !TRANSIENT_HTTP_STATUSES.has(status)) {
        return throwError(() => error);
      }
      // Jitter keeps several parallel refreshes from retrying in lockstep.
      return timer(baseDelayMs * 2 ** (attempt - 1) + Math.random() * (baseDelayMs / 2));
    },
  });

export const prepare = <T>(callback: () => void) => {
  return (source: Observable<T>): Observable<T> =>
    defer(() => {
      callback();
      return source;
    });
};

export const indicate = <T>(indicator: Subject<boolean>) => {
  return (source: Observable<T>): Observable<T> =>
    source.pipe(
      prepare(() => indicator.next(true)),
      finalize(() => indicator.next(false))
    );
};

export const indicateWithCounter = <T>(indicator: Subject<boolean>, counter: Counter) => {
  return (source: Observable<T>): Observable<T> =>
    source.pipe(
      prepare(() => {
        counter.increase();
        indicator.next(true);
      }),
      finalize(() => {
        if(counter.decrease() <= 0 ) {
          indicator.next(false);
        }
      })
    );
};

export class Counter {
  private counter = 0;

  increase(): number {
    this.counter++;
    return this.counter;
  }

  decrease(): number {
    this.counter--;

    if(this.counter < 0) {
      this.counter = 0;
    }

    return this.counter;
  }

  getValue(): number {
    return this.counter;
  }
}
